import { useState, useMemo } from "react";
import { Search, BookOpen, ChevronRight } from "lucide-react";
import { Input } from "./ui/input";

/**
 * OperationsGuide — the BTR Health "Bible".
 *
 * Single source-of-truth knowledge base for admins covering every feature,
 * the reason it exists, the NDIS rule it implements, and the operational
 * playbook. Sectioned, searchable, and structured so we can later expose an
 * "editable knowledge base" API (v2) without changing the UI contract.
 */

const SECTIONS = [
  {
    id: "overview",
    title: "What BTR Health is",
    icon: "🏥",
    body: [
      { kind: "p", text: "BTR Health is an NDIS-registered support-worker marketplace. We connect Australian NDIS participants with vetted independent and employed support workers, and we administer the compliance, billing and audit trail required to claim against an NDIS plan." },
      { kind: "p", text: "We are a Tier-1 registered provider. Every booking flows through the same compliance pipeline regardless of who initiated it (participant, worker, or admin). The pipeline is designed so a participant can never be billed against their NDIS plan for a service we cannot legally deliver." },
      { kind: "kvs", items: [
        ["Participants", "End users of the supports. Pay $0 platform fees. Plan-managed, NDIA-managed, self-managed and private."],
        ["Support Workers", "Independent (with ABN) or employed by BTR Health. All police-checked, NDIS-Worker-Screening cleared."],
        ["BTR Health (admin)", "Tier-1 provider responsible for compliance, billing, dispute resolution, and price-gate enforcement."],
      ]},
    ],
  },

  {
    id: "auth-signup",
    title: "Auth & Signup",
    icon: "🔐",
    body: [
      { kind: "h", text: "Why two separate signup flows?" },
      { kind: "p", text: "Participants and workers need different data, different consent, and different downstream gates. Conflating them caused the 2025 onboarding-funnel drop-off. We now route every \"Sign Up\" CTA to /get-started, which lets the user self-select their role before we ask for any sensitive details." },
      { kind: "h", text: "Participant signup (/register)" },
      { kind: "ul", items: [
        "Step 1 captures account + an explicit Terms & Privacy consent checkbox. Australian Privacy Principles require explicit consent before collecting NDIS data — implicit consent (\"by continuing you agree…\") was not enough.",
        "Step 2 captures NDIS plan type, NDIS number, DOB, address, and conditionally the Plan Manager contact (only for plan-managed plans).",
        "Google sign-in is accepted but the user is still required to complete Step 2 in /onboarding before they can book — this is non-negotiable for compliance.",
      ]},
      { kind: "h", text: "Worker signup (/worker-signup)" },
      { kind: "ul", items: [
        "8-step wizard: Welcome → Account → Personal Info → Photo → Services & Rate → Availability → Verification docs → About You.",
        "Step 5 (Services) requires the worker to pick their primary NDIS registration group (1 of 11). This single field sets their rate ceiling.",
        "Step 7 collects: NDIS Worker Screening, Police Check, First Aid (optional), Insurance (independents only), and a signed Worker Agreement.",
        "On submit the worker lands in approval_status=\"pending\". They cannot receive shifts until an admin approves them in the Worker Docs tab.",
      ]},
      { kind: "note", text: "Login uses bcrypt-hashed passwords + 7-day JWT in localStorage. Google OAuth is native (Google Identity Services), not a 3rd-party shim — we own the redirect_uri." },
    ],
  },

  {
    id: "verification",
    title: "Identity & Document Verification",
    icon: "🪪",
    body: [
      { kind: "h", text: "Three independent verifications" },
      { kind: "ul", items: [
        "Participant ID verification — Didit KYC SDK (driving licence / passport / Medicare). Webhook updates id_verification_status to verified/rejected. Required before the first booking.",
        "Participant Service Agreement — digital signature flow at /service-agreement. Generates a PDF (reportlab), emails the participant via Resend, and gates bookings on service_agreement_approved.",
        "Worker documents — admin reviews uploads in Admin → Worker Docs. Workers cannot accept shifts until verification_status=\"approved\". Police Check + NDIS Worker Screening are mandatory; First Aid is recommended.",
      ]},
      { kind: "h", text: "Why three?" },
      { kind: "p", text: "The NDIS Quality & Safeguards Commission requires a Tier-1 provider to verify (a) the participant is real and their NDIS number is current, (b) there is a written, signed service agreement on file, and (c) every worker delivering services is screened. Skipping any one of these would void the provider registration." },
    ],
  },

  {
    id: "ndis-pricing",
    title: "NDIS Pricing & Rate Caps",
    icon: "💰",
    body: [
      { kind: "h", text: "The 11 registration groups" },
      { kind: "p", text: "BTR Health is registered to deliver 11 of the 25 NDIS registration groups. Each group has a different weekday-daytime price ceiling published in the NDIS Pricing Arrangements 2026-27. The full table lives in /app/backend/ndis_pricing.py and is exposed at GET /api/pricing/categories." },
      { kind: "h", text: "Why the rate-cap gate exists" },
      { kind: "p", text: "Workers are free to set any rate they like — but if their rate exceeds the NDIS cap and a plan-managed or NDIA-managed participant tries to book them, the plan administrator will reject the invoice. We therefore HARD-BLOCK such bookings at /api/bookings with HTTP 403. Self-managed and private participants can bypass the cap because they pay out of pocket." },
      { kind: "h", text: "Where the gate fires" },
      { kind: "ul", items: [
        "Worker signup: validateStep(4) refuses to advance if rate > cap.",
        "Worker profile edit: backend POST/PUT both reject the save with a 400.",
        "Booking creation: backend POST /api/bookings returns 403 with NDIS-cap detail for plan-managed/NDIA-managed users.",
        "Booking UI: WorkerDetailPage dialog shows a red detail-cap-blocked banner before the user clicks Book.",
      ]},
      { kind: "note", text: "Cap engine policy: weekday_cap(category) returns the LOWEST standard-daytime cap across all line items in that registration group. This is intentionally conservative — high-intensity sub-categories (e.g. High-Intensity Daily Personal Activities at ~$94/hr) will require a future sub-category opt-in." },
    ],
  },

  {
    id: "bookings",
    title: "Bookings",
    icon: "📅",
    body: [
      { kind: "h", text: "Lifecycle" },
      { kind: "ul", items: [
        "pending → participant has requested. Worker has 24h to accept/decline. Plan-managed bookings receive a Resend email to the plan manager for billing.",
        "confirmed → worker accepted. Booking appears in both dashboards. SMS confirmation goes via Twilio.",
        "completed → worker marked complete and added shift notes. Triggers participant confirm/dispute window.",
        "cancelled → covered in the next section.",
      ]},
      { kind: "h", text: "Who can do what" },
      { kind: "kvs", items: [
        ["Participant", "Create, view, cancel, dispute. Confirm completion post-shift."],
        ["Worker", "Accept, decline, mark complete, add shift notes, raise incident."],
        ["Admin", "All of the above + create on behalf of participant, edit any field, cancel with NDIS rules, override status."],
      ]},
      { kind: "h", text: "Shift notes" },
      { kind: "p", text: "Workers can attach a free-text note when they mark a shift complete. From the Jobs tab they can tap \"✨ Format with AI\" — GPT-4o reformats the draft into the NDIS progress-note structure (Activities · Observations · Mood & Engagement · Incidents · Handover). Stored on bookings.shift_notes." },
    ],
  },

  {
    id: "cancellations",
    title: "Cancellations (NDIS Rules)",
    icon: "🚫",
    body: [
      { kind: "h", text: "The 7-clear-day rule" },
      { kind: "p", text: "Under the NDIS Pricing Arrangements 2026-27 Part B §8, a participant-initiated cancellation within 7 clear days of the scheduled start time is \"short notice\" and the provider may claim 100% of the agreed fee." },
      { kind: "h", text: "Decision matrix" },
      { kind: "table", headers: ["Initiated by", "Notice", "Billable", "Stored as"], rows: [
        ["Participant", "≥ 7 days", "❌ No", "short_notice=false"],
        ["Participant", "< 7 days", "✅ Yes (100%)", "short_notice=true, billable_amount=total_cost"],
        ["No-show", "Any", "✅ Yes (100%)", "short_notice=true, cancelled_by=no_show"],
        ["Worker", "Any", "❌ No", "short_notice=false"],
        ["Admin / BTR Health", "Any", "❌ No", "short_notice=false"],
      ]},
      { kind: "h", text: "What's stored on the booking" },
      { kind: "p", text: "Every cancellation persists a cancellation object containing: cancelled_by, cancelled_at, reason, hours_until_shift, short_notice, billable, billable_amount, and a human-readable policy_reason. Finance reconciles billable cancellations into the monthly invoice run." },
      { kind: "note", text: "Soft cap: 12 short-notice cancellations per participant per financial year (Jul–Jun). When approached, Admin → Bookings surfaces a yellow warning. We do not auto-block — we surface and let the admin decide whether to chase, refer to support coordination, or pause." },
    ],
  },

  {
    id: "payments",
    title: "Payments & Invoicing",
    icon: "💳",
    body: [
      { kind: "h", text: "Three billing paths" },
      { kind: "ul", items: [
        "Plan-managed → Plan manager is emailed an invoice (Resend) on shift completion. Funds flow plan-manager → BTR Health → worker (independent) or BTR Health pay-cycle (employed).",
        "NDIA-managed → BTR Health claims directly via the NDIS portal. The participant never sees a transaction.",
        "Self-managed / private → Stripe checkout. The participant pays the booking total upfront; the funds are escrowed until shift completion + 48h dispute window.",
      ]},
      { kind: "h", text: "Where money sits" },
      { kind: "p", text: "Stripe payments hit a BTR Health platform account. Worker payouts are processed weekly on Wednesdays. Independent contractors invoice us via PDF generated by /api/admin/invoices; employed workers receive a payslip via the standard payroll run." },
    ],
  },

  {
    id: "messaging",
    title: "Messaging",
    icon: "💬",
    body: [
      { kind: "p", text: "Real-time chat between participants and workers (and admin) is handled by a WebSocket layer at /ws/chat. A polling fallback fires every 6s when the socket cannot connect, so the user experience degrades gracefully on flaky mobile networks." },
      { kind: "p", text: "Conversations are scoped to a participant–worker pair plus an admin can join any thread. Messages are stored in MongoDB conversations + messages collections. There is no end-to-end encryption — admins can read every thread for safeguarding purposes (this is disclosed in the Terms)." },
    ],
  },

  {
    id: "admin-rights",
    title: "Admin Powers & Audit Trail",
    icon: "🛡️",
    body: [
      { kind: "h", text: "What admin can do" },
      { kind: "ul", items: [
        "Approve / reject worker applications and documents.",
        "Approve / reject participant Service Agreements and ID Verifications (Didit override).",
        "Create, edit, cancel any booking (NDIS rules still computed automatically).",
        "View all bookings, messages, incidents, invoices.",
        "Run the data-cleanup utilities in Overview (Google-user purge, full reseed).",
      ]},
      { kind: "h", text: "Audit trail" },
      { kind: "p", text: "Every admin-mutating action stamps the booking/user with admin_creator_id, updated_at, and (for cancellations) the full cancellation outcome including policy_reason. The analytics_events collection logs every admin click for safeguarding audits." },
    ],
  },

  {
    id: "incidents",
    title: "Incidents",
    icon: "⚠️",
    body: [
      { kind: "h", text: "The 5 NDIS reportable categories" },
      { kind: "p", text: "The NDIS Quality & Safeguards Commission recognises exactly five reportable-incident categories. Miscategorisation is the #1 audit failure — always pick the closest match, then let an admin override with a reason if needed." },
      { kind: "ul", items: [
        "Death of a participant — 24h notification window",
        "Serious injury of a participant — 5 business days",
        "Abuse or neglect of a participant — 5 business days",
        "Unlawful sexual or physical contact / assault — 24h",
        "Unauthorised restrictive practice — 5 business days",
      ]},
      { kind: "p", text: "Non-notifiable events (e.g. property damage, minor conduct) go into the sixth 'Other — internal record only' bucket so we still keep a log without triggering Commission notification." },
      { kind: "h", text: "Admin controls" },
      { kind: "ul", items: [
        "Edit any field · audit-logged with old→new values.",
        "Override category / is_reportable · reason required.",
        "Reassign owner · move follow-up between admins.",
        "Reopen a closed incident · reason required; status → under_investigation.",
        "Merge duplicates · tombstones the duplicate; preserves full snapshot in the master's addendum log.",
        "Add note · appended with timestamp + admin name; audit-logged.",
      ]},
      { kind: "h", text: "Audit trail" },
      { kind: "p", text: "Every change stamps who / what changed (old → new) / when / (reason where required). The audit_log is APPEND-ONLY — no admin, ever, can edit or delete an entry. The immutable feed sits at the bottom of every incident detail view." },
      { kind: "h", text: "Compliance dashboard" },
      { kind: "ul", items: [
        "Totals — month / quarter / year, broken down by category.",
        "Reportable timeliness — on time vs late vs missed. This is the FIRST thing Commission auditors check.",
        "Average time from date_became_aware to commission_notified_at.",
        "Open incidents by age — >5 days and >30 days (ageing = audit red flag).",
        "Patterns — top workers and top participants by incident count (repeat >=3 flagged red).",
      ]},
      { kind: "h", text: "Exports" },
      { kind: "p", text: "One-click CSV, Excel (openpyxl-formatted headers) and per-incident landscape PDF exports of the full register — filter by date range for a specific registration cycle. Hand any of these formats to your NDIS registration consultant." },
      { kind: "h", text: "Permissions" },
      { kind: "kvs", items: [
        ["Worker / Participant", "Create own, view own, read-only after submit. No visibility into others' records, no compliance dashboard, no exports."],
        ["Admin (Trent, Rashaun, Beshoy)", "Full CRUD, override controls, compliance dashboard, all exports, alert-settings drawer."],
      ]},
    ],
  },

  {
    id: "analytics",
    title: "Analytics",
    icon: "📊",
    body: [
      { kind: "p", text: "We run a custom internal analytics layer instead of a third-party tracker — both for cost reasons and to avoid leaking participant data to ad networks. Every page view + named CTA click goes through track() in /utils/analytics.js → POST /api/analytics/track." },
      { kind: "p", text: "Admin → Analytics aggregates by funnel step (register → onboarding → first booking) and by acquisition source. Free-text notes (path=/, search=null) mean a homepage view with no UTM tags — those are direct or unsourced traffic." },
    ],
  },

  {
    id: "data-model",
    title: "Data Model Cheat-Sheet",
    icon: "🗄️",
    body: [
      { kind: "h", text: "Core collections" },
      { kind: "kvs", items: [
        ["users", "Participants, workers, admins. Includes NDIS plan type, ESA approval, ID verification status."],
        ["workers", "Public-facing worker profiles. Linked to users via user_id. Contains hourly_rate + primary_service_category for the price gate."],
        ["bookings", "Every booking. cancellation sub-object present once cancelled. shift_notes populated post-completion."],
        ["conversations + messages", "Chat threads."],
        ["analytics_events", "Internal page-view + CTA tracker."],
        ["incidents", "NDIS Quality & Safeguards reporting."],
        ["didit_events", "Webhook receipts for KYC."],
        ["invoices, payment_transactions", "Stripe + plan-manager invoicing."],
      ]},
    ],
  },
];


export function OperationsGuide() {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState("overview");

  const filtered = useMemo(() => {
    if (!query.trim()) return SECTIONS;
    const q = query.toLowerCase();
    return SECTIONS.filter((s) => {
      if (s.title.toLowerCase().includes(q)) return true;
      return s.body.some((blk) => {
        if (blk.text && blk.text.toLowerCase().includes(q)) return true;
        if (blk.items && blk.items.some((it) => (Array.isArray(it) ? it.join(" ") : it).toLowerCase().includes(q))) return true;
        if (blk.rows && blk.rows.some((row) => row.join(" ").toLowerCase().includes(q))) return true;
        return false;
      });
    });
  }, [query]);

  const active = filtered.find((s) => s.id === activeId) || filtered[0] || SECTIONS[0];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-5">
      {/* Sidebar */}
      <aside className="bg-white rounded-2xl border border-slate-100 p-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
        <div className="flex items-center gap-2 px-2 py-1.5 mb-2 text-slate-900">
          <BookOpen className="w-4 h-4 text-[#5BA3C0]" />
          <h3 className="text-sm font-semibold">Operations Guide</h3>
        </div>
        <div className="relative mb-2">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the bible…"
            className="h-9 pl-8 text-sm"
            data-testid="ops-guide-search"
          />
        </div>
        <nav className="space-y-0.5">
          {filtered.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              className={`w-full text-left px-2.5 py-1.5 rounded-md text-xs flex items-center justify-between gap-2 transition-colors ${
                active?.id === s.id ? "bg-[#87CEEB]/15 text-[#5BA3C0] font-semibold" : "text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`ops-guide-section-${s.id}`}
            >
              <span className="flex items-center gap-1.5"><span>{s.icon}</span> {s.title}</span>
              <ChevronRight className={`w-3 h-3 ${active?.id === s.id ? "opacity-100" : "opacity-0"}`} />
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-2.5 py-3 text-xs text-slate-400">No matches.</p>
          )}
        </nav>
      </aside>

      {/* Body */}
      <article className="bg-white rounded-2xl border border-slate-100 p-6 lg:p-8">
        {active ? (
          <>
            <header className="mb-5 pb-3 border-b border-slate-100">
              <p className="text-[11px] uppercase tracking-wider text-[#5BA3C0] font-semibold">BTR Health · Operations Guide</p>
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mt-1">
                <span>{active.icon}</span> {active.title}
              </h2>
            </header>
            <div className="prose prose-slate max-w-none space-y-4">
              {active.body.map((blk, i) => renderBlock(blk, i))}
            </div>
            <footer className="mt-8 pt-4 border-t border-slate-100 text-[11px] text-slate-400">
              Last updated Feb 2026 — internal use only. Email <a href="mailto:admin@btrhealth.com.au" className="underline">admin@btrhealth.com.au</a> with corrections.
            </footer>
          </>
        ) : (
          <p className="text-slate-400">Pick a section to start.</p>
        )}
      </article>
    </div>
  );
}


function renderBlock(blk, i) {
  if (blk.kind === "h") {
    return <h3 key={i} className="text-base font-bold text-slate-900 mt-4">{blk.text}</h3>;
  }
  if (blk.kind === "p") {
    return <p key={i} className="text-sm text-slate-600 leading-relaxed">{blk.text}</p>;
  }
  if (blk.kind === "ul") {
    return (
      <ul key={i} className="list-disc pl-5 space-y-1.5 text-sm text-slate-600">
        {blk.items.map((it, j) => <li key={j}>{it}</li>)}
      </ul>
    );
  }
  if (blk.kind === "kvs") {
    return (
      <dl key={i} className="grid grid-cols-1 sm:grid-cols-[180px,1fr] gap-x-4 gap-y-2 bg-slate-50 rounded-lg p-3">
        {blk.items.map(([k, v], j) => (
          <div key={j} className="contents">
            <dt className="text-sm font-semibold text-slate-900">{k}</dt>
            <dd className="text-sm text-slate-600">{v}</dd>
          </div>
        ))}
      </dl>
    );
  }
  if (blk.kind === "table") {
    return (
      <div key={i} className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {blk.headers.map((h, j) => <th key={j} className="text-left p-2 font-semibold text-slate-700">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {blk.rows.map((row, j) => (
              <tr key={j} className="border-b border-slate-100">
                {row.map((c, k) => <td key={k} className="p-2 text-slate-600">{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (blk.kind === "note") {
    return (
      <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
        <span className="font-semibold">Note:</span> {blk.text}
      </div>
    );
  }
  return null;
}
